import { PermissionGuard } from '@/components/PermissionGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useNotificationSettings } from '@/hooks/useNotificationSettings';
import { useUpdateNotificationSettings } from '@/hooks/useNotificationSettingsActions';
import { useUserOrganizationRole } from '@/hooks/useUserRole';
import { getUserRole } from '@/lib/auth/token';
import type { NotificationSettings } from '@octave/contracts';
import type { OrganizationMember } from '@octave/contracts';
import { canWrite } from '@/lib/roleMapping';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Mail, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface DraftRecipient {
  key: string;
  type: 'member' | 'custom';
  userId: string | null;
  email: string | null;
  enabled: boolean;
}

interface Draft {
  emailEnabled: boolean;
  recipients: DraftRecipient[];
}

function toDraft(settings: NotificationSettings): Draft {
  return {
    emailEnabled: settings.emailEnabled,
    recipients: settings.recipients.map((recipient) => ({
      key: recipient.id,
      type: recipient.type,
      userId: recipient.type === 'member' ? recipient.userId : null,
      email: recipient.email,
      enabled: recipient.enabled,
    })),
  };
}

const customEmailSchema = z.object({
  email: z.string().email('Adresse email invalide'),
});

type CustomEmailFormData = z.infer<typeof customEmailSchema>;

interface NotificationsSectionProps {
  organizationId: string | undefined;
  members: OrganizationMember[] | undefined;
}

export function NotificationsSection({
  organizationId,
  members,
}: NotificationsSectionProps) {
  const {
    data: settings,
    isLoading,
    isError,
  } = useNotificationSettings(organizationId);
  const { mutate: updateSettings, isPending: isSaving } =
    useUpdateNotificationSettings();

  const userRole = getUserRole();
  const userOrganizationRole = useUserOrganizationRole();
  const canEdit = canWrite(userRole, userOrganizationRole);

  // No save button: every change is persisted right away. `pending` holds
  // the optimistic state while the request is in flight, then gives way to
  // the server data (or to it again on failure, which reverts the change).
  const [pending, setPending] = useState<Draft | null>(null);
  const serverDraft = useMemo(
    () => (settings ? toDraft(settings) : null),
    [settings]
  );
  const current = pending ?? serverDraft;

  /** Renders the change immediately, then saves the whole state. */
  const persist = (next: Draft) => {
    if (!organizationId) return;
    setPending(next);
    updateSettings(
      {
        organizationId,
        emailEnabled: next.emailEnabled,
        recipients: next.recipients.map((recipient) =>
          recipient.type === 'member'
            ? {
                type: 'member' as const,
                userId: recipient.userId!,
                enabled: recipient.enabled,
              }
            : {
                type: 'custom' as const,
                email: recipient.email!,
                enabled: recipient.enabled,
              }
        ),
      },
      // Both paths drop the optimistic copy: on success the server data
      // already matches, on failure falling back to it undoes the change.
      { onSettled: () => setPending(null) }
    );
  };

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addMode, setAddMode] = useState<'member' | 'custom'>('member');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  const customEmailForm = useForm<CustomEmailFormData>({
    resolver: zodResolver(customEmailSchema),
    defaultValues: { email: '' },
  });

  const memberByUserId = useMemo(() => {
    const map = new Map<string, OrganizationMember>();
    members?.forEach((member) => map.set(member.userId, member));
    return map;
  }, [members]);

  const availableMembers = useMemo(() => {
    const alreadyAdded = new Set(
      current?.recipients
        .filter((r) => r.type === 'member')
        .map((r) => r.userId)
    );
    const alreadyAddedEmails = new Set(
      current?.recipients
        .map((r) => r.email?.toLowerCase())
        .filter((email): email is string => !!email)
    );
    return (members ?? []).filter(
      (member) =>
        !member.isDisabled &&
        !alreadyAdded.has(member.userId) &&
        (!member.email || !alreadyAddedEmails.has(member.email.toLowerCase()))
    );
  }, [members, current]);

  const closeAddDialog = () => {
    setIsAddDialogOpen(false);
    setSelectedMemberId(null);
    setAddMode('member');
    customEmailForm.reset();
  };

  const addRecipient = (recipient: DraftRecipient) => {
    if (!current) return;
    persist({
      ...current,
      recipients: [...current.recipients, recipient],
    });
  };

  const handleAddMember = () => {
    if (!selectedMemberId) return;
    const member = memberByUserId.get(selectedMemberId);
    addRecipient({
      key: crypto.randomUUID(),
      type: 'member',
      userId: selectedMemberId,
      email: member?.email ?? null,
      enabled: true,
    });
    closeAddDialog();
  };

  const handleAddCustomEmail = customEmailForm.handleSubmit(({ email }) => {
    const normalized = email.trim().toLowerCase();
    const alreadyExists = current?.recipients.some(
      (r) => r.email?.toLowerCase() === normalized
    );
    if (alreadyExists) {
      customEmailForm.setError('email', {
        message: 'Cette adresse est déjà destinataire',
      });
      return;
    }
    addRecipient({
      key: crypto.randomUUID(),
      type: 'custom',
      userId: null,
      email: normalized,
      enabled: true,
    });
    closeAddDialog();
  });

  const toggleRecipient = (key: string, enabled: boolean) => {
    if (!current) return;
    persist({
      ...current,
      recipients: current.recipients.map((r) =>
        r.key === key ? { ...r, enabled } : r
      ),
    });
  };

  const removeRecipient = (key: string) => {
    if (!current) return;
    persist({
      ...current,
      recipients: current.recipients.filter((r) => r.key !== key),
    });
  };

  const recipientLabel = (recipient: DraftRecipient): string => {
    if (recipient.type === 'custom') return recipient.email ?? '-';
    const member = recipient.userId
      ? memberByUserId.get(recipient.userId)
      : undefined;
    if (member?.firstName && member?.lastName) {
      return `${member.firstName} ${member.lastName}`;
    }
    return recipient.email ?? 'Membre inconnu';
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-slate-900">
          Alertes de consommation
        </h2>
        {isSaving && (
          <span className="flex items-center gap-1.5 text-xs text-slate-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Enregistrement
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : isError || !current ? (
        <p className="text-sm text-red-500 py-4">
          Erreur lors du chargement des paramètres de notification
        </p>
      ) : (
        <div className="space-y-2">
          {/* Email + recipients */}
          <div className="bg-white rounded-xl px-5 py-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <Mail className="h-4.5 w-4.5 text-emerald-700" />
              </div>
              <div className="min-w-0 flex-1">
                <Label
                  htmlFor="notifications-email"
                  className="text-sm font-medium text-slate-900"
                >
                  Notifications email
                </Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Envoyées aux destinataires ci-dessous quand une ligne atteint
                  un seuil de consommation
                </p>
              </div>
              <Switch
                id="notifications-email"
                checked={current.emailEnabled}
                disabled={!canEdit || isSaving}
                onCheckedChange={(checked) =>
                  persist({ ...current, emailEnabled: checked })
                }
              />
            </div>

            <div className="mt-4 border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-slate-900">
                  Destinataires
                </p>
                <PermissionGuard permission="write">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 rounded-full"
                    disabled={isSaving}
                    onClick={() => setIsAddDialogOpen(true)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Ajouter un destinataire
                  </Button>
                </PermissionGuard>
              </div>

              {!current.emailEnabled && current.recipients.length > 0 && (
                <p className="text-xs text-amber-600 mb-3">
                  Les notifications email sont désactivées, aucun email ne sera
                  envoyé aux destinataires.
                </p>
              )}

              {current.recipients.length === 0 ? (
                <p className="text-sm text-slate-500 py-2">
                  Aucun destinataire configuré : personne ne reçoit les alertes
                </p>
              ) : (
                <div className="space-y-2">
                  {current.recipients.map((recipient) => (
                    <div
                      key={recipient.key}
                      className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2.5"
                    >
                      <Badge
                        variant="outline"
                        className={
                          recipient.type === 'member'
                            ? 'text-xs bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0'
                            : 'text-xs bg-violet-50 text-violet-600 border-violet-200 shrink-0'
                        }
                      >
                        {recipient.type === 'member' ? 'Membre' : 'Externe'}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-900 truncate">
                          {recipientLabel(recipient)}
                        </p>
                        {recipient.type === 'member' && (
                          <p
                            className={`text-xs truncate ${
                              recipient.email
                                ? 'text-slate-500'
                                : 'text-amber-600'
                            }`}
                          >
                            {recipient.email ?? 'Adresse email manquante'}
                          </p>
                        )}
                      </div>
                      <Switch
                        checked={recipient.enabled}
                        disabled={!canEdit || isSaving}
                        onCheckedChange={(checked) =>
                          toggleRecipient(recipient.key, checked)
                        }
                        aria-label={`Activer les notifications pour ${recipientLabel(recipient)}`}
                      />
                      <PermissionGuard permission="write">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-600"
                              disabled={isSaving}
                              onClick={() => removeRecipient(recipient.key)}
                              aria-label={`Supprimer le destinataire ${recipientLabel(recipient)}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Supprimer</TooltipContent>
                        </Tooltip>
                      </PermissionGuard>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add recipient dialog */}
      <Dialog
        open={isAddDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeAddDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un destinataire</DialogTitle>
            <DialogDescription>
              Choisissez un membre de l'organisation ou saisissez une adresse
              email externe.
            </DialogDescription>
          </DialogHeader>
          <Tabs
            value={addMode}
            onValueChange={(value) => setAddMode(value as 'member' | 'custom')}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="member">Membre</TabsTrigger>
              <TabsTrigger value="custom">Email externe</TabsTrigger>
            </TabsList>
            <TabsContent value="member" className="space-y-4 pt-3">
              {availableMembers.length === 0 ? (
                <p className="text-sm text-slate-500 py-2">
                  Tous les membres actifs sont déjà destinataires
                </p>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="recipient-member">Membre</Label>
                  <Select
                    value={selectedMemberId ?? undefined}
                    onValueChange={setSelectedMemberId}
                  >
                    <SelectTrigger id="recipient-member" className="w-full">
                      <SelectValue placeholder="Sélectionner un membre" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMembers.map((member) => (
                        <SelectItem key={member.userId} value={member.userId}>
                          {member.firstName && member.lastName
                            ? `${member.firstName} ${member.lastName}`
                            : (member.email ?? member.userId)}
                          {member.email &&
                            member.firstName &&
                            ` (${member.email})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={closeAddDialog}>
                  Annuler
                </Button>
                <Button
                  onClick={handleAddMember}
                  disabled={!selectedMemberId}
                >
                  Ajouter
                </Button>
              </DialogFooter>
            </TabsContent>
            <TabsContent value="custom" className="pt-3">
              <form
                onSubmit={handleAddCustomEmail}
                className="space-y-4"
                noValidate
              >
                <div className="space-y-1.5">
                  <Label htmlFor="recipient-email">Adresse email</Label>
                  <Input
                    id="recipient-email"
                    type="email"
                    placeholder="prenom.nom@entreprise.fr"
                    {...customEmailForm.register('email')}
                  />
                  {customEmailForm.formState.errors.email && (
                    <p className="text-xs text-red-500">
                      {customEmailForm.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeAddDialog}
                  >
                    Annuler
                  </Button>
                  <Button type="submit">Ajouter</Button>
                </DialogFooter>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </section>
  );
}
